import { Link } from "react-router-dom";
import {
  ArrowRight,
  Award,
  Building2,
  Star,
  FileCheck,
  HelpCircle,
  Mail,
  Workflow,
  CalendarCheck,
  MapPin,
  ShieldCheck,
} from "lucide-react";
import { brand, founder } from "@/data/site/brand";

interface Props {
  onClose: () => void;
}

const aboutLinks = [
  {
    icon: Building2,
    name: "About Our Firm",
    description: "Who we are, our credentials, and how we work with clients.",
    href: "/about",
  },
  {
    icon: Workflow,
    name: "How It Works",
    description: "Our 3-step process from first call to IRS resolution.",
    href: "/how-it-works",
  },
  {
    icon: FileCheck,
    name: "Case Results",
    description: "Real outcomes from real clients — dollars saved, liens released.",
    href: "/results",
  },
  {
    icon: Star,
    name: "Client Reviews",
    description: "Five-star reviews from taxpayers we've represented.",
    href: "/reviews",
  },
  {
    icon: CalendarCheck,
    name: "Free Consultation",
    description: "Book a 15-minute call with a CPA — no pressure, honest answers.",
    href: "/free-consultation",
  },
  {
    icon: MapPin,
    name: "Areas We Serve",
    description: "Prosper, TX HQ — representing clients nationwide before the IRS.",
    href: "/service-areas",
  },
  {
    icon: HelpCircle,
    name: "Frequently Asked Questions",
    description: "Answers to common questions about IRS issues and tax resolution.",
    href: "/faq",
  },
  {
    icon: Mail,
    name: "Contact Us",
    description: "Phone, email, and office hours — reach our team directly.",
    href: "/contact",
  },
];

export const SiteAboutDropdown = ({ onClose }: Props) => {
  return (
    <div className="bg-white border-b border-border shadow-xl">
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Founder feature card — matches Resources featured card */}
          <Link
            to="/about/franklin-sofi"
            onClick={onClose}
            className="col-span-12 lg:col-span-4 group relative overflow-hidden rounded-xl bg-gradient-navy text-white p-6 flex flex-col justify-between hover:shadow-xl transition-shadow"
          >
            <div
              className="absolute inset-0 opacity-[0.08] pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 20%, hsl(var(--gold)) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/80 mb-3">
                <Award className="w-3 h-3" /> Meet the Founder
              </span>
              <h3 className="font-heading text-xl font-bold leading-tight">
                {founder.name}
              </h3>
              <p className="text-[13px] text-white/75 mt-2 leading-snug">
                {founder.title} with {brand.yearsExperience}+ years representing
                taxpayers before the IRS. CPA · CTRS · ASTPS Gold Member.
              </p>
            </div>
            <div className="relative mt-5 flex items-center gap-3 rounded-lg bg-white/10 px-3 py-2.5 group-hover:bg-white/15 transition-colors">
              <img
                src={founder.image}
                alt={founder.name}
                className="w-8 h-8 rounded-full object-cover border border-gold shrink-0"
                width={32}
                height={32}
              />
              <span className="text-sm font-semibold flex-1">Read full bio</span>
              <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-0.5 transition-transform" />
            </div>
          </Link>

          {/* Right grid — identical pattern to Resources dropdown */}
          <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-1">
            {aboutLinks.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className="group flex gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors"
              >
                <div className="shrink-0 w-10 h-10 rounded-lg bg-secondary flex items-center justify-center text-navy group-hover:bg-gold group-hover:text-navy-dark transition-colors">
                  <item.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-navy group-hover:text-navy-dark transition-colors leading-tight">
                    {item.name}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Footer strip — identical pattern to Resources dropdown */}
        <div className="mt-6 pt-5 border-t border-border flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-gold" />
            <strong className="text-navy">{brand.yearsExperience}+ years</strong>{" "}
            of IRS controversy experience · CPA · CTRS · ASTPS Gold Member
          </div>
          <Link
            to="/free-consultation"
            onClick={onClose}
            className="inline-flex items-center gap-2 text-sm font-semibold text-navy uppercase tracking-wider hover:text-navy-dark transition-colors"
          >
            Free Case Review <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};
